import type { LotteryMachineConfig, TimingConfig } from './types'

/**
 * 搅拌式摇奖机官方/公开尺寸（米）
 * - 球舱内径 700mm、球径 50mm：官方
 * - 大盘 ≈500mm、小盘 ≈260mm：照片比例估算
 * - 出球口 ≈62mm、底座外宽 750mm
 */
export const DEFAULT_CONFIG: LotteryMachineConfig = {
  mode: 'physics_result',
  gravityY: -9.81,
  ballRadius: 0.025, // Ø50mm
  ballMass: 0.025,
  ballMassJitter: 0.0005,
  ballRestitution: 0.48,
  ballFriction: 0.55,
  wallRestitution: 0.42,
  wallFriction: 0.28,
  diskRestitution: 0.18,
  diskFriction: 0.92,
  diskOmegaTarget: 16,
  chamberInnerDiameter: 0.7, // Ø700mm
  outerDiskDiameter: 0.21, // Ø210mm（不改）
  innerDiskDiameter: 0.109, // Ø109mm（不改）
  exitChannelWidth: 0.062, // 62mm
  baseOuterWidth: 0.75, // 750mm
  timing: {
    dropIn: 6.5,
    diskSpinup: 1.0,
    mix: 10.0,
    gateOpenAnim: 0.25,
    drawTimeout: 7.0,
    capture: 0.35,
    railTravel: 2.2,
    showHold: 0.9,
    betweenDraws: 0.5,
    diskSpindown: 1.0,
    retryBoost: 1.5,
  },
  maxDrawRetry: 3,
  redCount: 6,
  blueCount: 1,
  redPoolSize: 33,
  bluePoolSize: 16,
  fixedDt: 1 / 120,
  maxSubSteps: 6,
}

export type PartialLotteryConfig = Partial<Omit<LotteryMachineConfig, 'timing'>> & {
  timing?: Partial<TimingConfig>
}

export function mergeConfig(partial?: PartialLotteryConfig): LotteryMachineConfig {
  if (!partial) return { ...DEFAULT_CONFIG, timing: { ...DEFAULT_CONFIG.timing } }
  return {
    ...DEFAULT_CONFIG,
    ...partial,
    timing: { ...DEFAULT_CONFIG.timing, ...partial.timing },
  }
}

export function generatePlannedResult(
  redCount: number,
  blueCount: number,
  redPool: number,
  bluePool: number,
): { red: number[]; blue: number[] } {
  const pick = (pool: number, count: number) => {
    const arr = Array.from({ length: pool }, (_, i) => i + 1)
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1))
      ;[arr[i], arr[j]] = [arr[j]!, arr[i]!]
    }
    return arr.slice(0, count)
  }
  return {
    red: pick(redPool, redCount),
    blue: pick(bluePool, blueCount),
  }
}

export function assertPlanned(
  red: number[],
  blue: number[],
  config: LotteryMachineConfig,
): void {
  if (red.length !== config.redCount) throw new Error(`红球数量应为 ${config.redCount}`)
  if (blue.length !== config.blueCount) throw new Error(`蓝球数量应为 ${config.blueCount}`)
  if (new Set(red).size !== red.length) throw new Error('红球号码不可重复')
  for (const n of red) {
    if (n < 1 || n > config.redPoolSize) throw new Error(`红球号码越界: ${n}`)
  }
  for (const n of blue) {
    if (n < 1 || n > config.bluePoolSize) throw new Error(`蓝球号码越界: ${n}`)
  }
}

export function uid(prefix = 's'): string {
  return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`
}
